import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../ApplicationState';
import { selectClassroom } from '../Classrooms';
import StudentCheckInGrid from './StudentCheckInGrid';
import ElementaryCheckInModal from './ElementaryCheckInModal';
import CheckInSidePanel from './CheckInSidePanel';
import ConfirmationModal from '../components/ConfirmationModal';
import { isUserHasKey } from '../GlobalFunctions';
import { getJournals, recordElementaryJournalEntry } from '../BackendInterface';
import { moodToNumberMap } from '../ClassroomDashboard/PeekavilleDataHelpers';
import { Mood } from '../../peekapak-types/Mood';
import type { StudentProxyType } from '../../peekapak-types/DataProtocolTypes';
import type { FeelingsCheckinDataPoint } from '../../peekapak-types/PeekavilleGameDataTypes';
import styles from './TeacherCheckInTool.module.scss';

export interface StudentWithCheckIns extends StudentProxyType {
  todaysCheckIns: FeelingsCheckinDataPoint[];
}

const TeacherCheckInTool: React.FC = () => {
  const [students, setStudents] = useState<StudentWithCheckIns[]>([]);
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isLoadingStudents, setIsLoadingStudents] = useState(true);
  const [isLoadingCheckIns, setIsLoadingCheckIns] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);

  const isConfirmingRef = useRef(false);

  const classroom = useSelector((state: RootState) => selectClassroom(state));
  const hasAccess = isUserHasKey('KEY_TEACHER_CHECK-IN') || isUserHasKey('KEY_MASTER_KEY');

  useEffect(() => {
    if (classroom && hasAccess) {
      loadClassroomData();
    }
  }, [classroom]);

  const loadClassroomData = async () => {
    if (!classroom) return;

    setIsLoadingStudents(true);
    try {
      // Get students from classroom
      const studentsWithCheckIns: StudentWithCheckIns[] = (classroom.listOfStudentProxies || []).map((student) => ({
        ...student,
        todaysCheckIns: [],
      }));

      // Load today's check-ins for each student
      setIsLoadingCheckIns(true);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayTimestamp = today.getTime();

      for (const student of studentsWithCheckIns) {
        try {
          const journals = await getJournals(student.userId);
          // Filter for today's feelings check-ins only
          const todaysEntries = journals.filter((entry): entry is FeelingsCheckinDataPoint => {
            const entryDate = new Date(entry.createdAt);
            entryDate.setHours(0, 0, 0, 0);
            return entryDate.getTime() === todayTimestamp && entry.type === 'feelings-checkin';
          });
          student.todaysCheckIns = todaysEntries;
        } catch (error) {
          console.error(`Failed to load check-ins for student ${student.userId}:`, error);
        }
      }

      setStudents(studentsWithCheckIns);
    } catch (error) {
      console.error('Failed to load classroom data:', error);
    } finally {
      setIsLoadingStudents(false);
      setIsLoadingCheckIns(false);
    }
  };

  const handleStudentSelect = (studentId: string) => {
    console.log('handleStudentSelect called with:', studentId);

    // If this student is already selected and we're not showing modals,
    // reset and re-select to ensure proper state updates
    if (selectedStudentId === studentId && !isConfirmModalOpen && !isModalOpen) {
      setSelectedStudentId(null);
      setTimeout(() => {
        setSelectedStudentId(studentId);
        const student = students.find((s) => s.userId === studentId);
        if (student && student.todaysCheckIns && student.todaysCheckIns.length > 0) {
          setIsConfirmModalOpen(true);
        } else {
          setIsModalOpen(true);
        }
      }, 0);
    } else {
      setSelectedStudentId(studentId);
      const student = students.find((s) => s.userId === studentId);
      if (student && student.todaysCheckIns && student.todaysCheckIns.length > 0) {
        setIsConfirmModalOpen(true);
      } else {
        setIsModalOpen(true);
      }
    }
  };

  const handleConfirmReCheckIn = () => {
    console.log('handleConfirmReCheckIn called');
    isConfirmingRef.current = true;
    // Open mood modal after a brief delay to ensure no overlap
    setTimeout(() => {
      console.log('Opening mood modal after confirmation');
      setIsModalOpen(true);
      isConfirmingRef.current = false;
    }, 50);
  };

  const handleModalHide = () => {
    console.log('handleModalHide called, isConfirming:', isConfirmingRef.current);
    setIsConfirmModalOpen(false);
    // Only clear selection if we're not confirming (i.e., user canceled)
    if (!isConfirmingRef.current) {
      setSelectedStudentId(null);
    }
  };

  const handleMoodSelect = async (mood: Mood) => {
    if (!selectedStudentId) return;

    setIsSubmitting(true);
    try {
      const moodNumber = moodToNumberMap[mood as keyof typeof moodToNumberMap];
      if (moodNumber === undefined) {
        throw new Error(`Invalid mood for elementary check-in: ${mood}`);
      }
      await recordElementaryJournalEntry(
        selectedStudentId,
        moodNumber, // Numeric value (0-6)
        '', // Empty journal entry for elementary students
        false, // isTeacherRequested
      );

      // Refresh check-ins for this student
      await refreshStudentCheckIns(selectedStudentId);

      setIsModalOpen(false);
      setSelectedStudentId(null); // Reset selection after successful check-in
    } catch (error) {
      console.error('Failed to submit check-in:', error);
      alert('Failed to submit check-in. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const refreshStudentCheckIns = async (studentId: string) => {
    try {
      const journals = await getJournals(studentId);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayTimestamp = today.getTime();

      const todaysEntries = journals.filter((entry): entry is FeelingsCheckinDataPoint => {
        const entryDate = new Date(entry.createdAt);
        entryDate.setHours(0, 0, 0, 0);
        return entryDate.getTime() === todayTimestamp && entry.type === 'feelings-checkin';
      });

      setStudents((prevStudents) =>
        prevStudents.map((student) =>
          student.userId === studentId ? { ...student, todaysCheckIns: todaysEntries } : student,
        ),
      );
    } catch (error) {
      console.error(`Failed to refresh check-ins for student ${studentId}:`, error);
    }
  };

  // Toggle side panel
  const toggleSidePanel = () => {
    setIsSidePanelOpen(!isSidePanelOpen);
  };

  // Only find student if we have a valid selectedStudentId
  const selectedStudent = selectedStudentId ? students.find((s) => s.userId === selectedStudentId) : null;

  // Debug logging
  useEffect(() => {
    console.log('State update:', {
      selectedStudentId,
      selectedStudent: selectedStudent ? selectedStudent.firstName : 'none',
      isConfirmModalOpen,
      isModalOpen,
    });
  }, [selectedStudentId, isConfirmModalOpen, isModalOpen, selectedStudent]);

  if (!hasAccess) {
    return (
      <div className={styles.accessRestricted}>
        <div className={styles.messageBox}>
          <h2>Access Restricted</h2>
          <p>You need the Check-In Tool license to access this feature.</p>
        </div>
      </div>
    );
  }

  if (!classroom) {
    return (
      <div className={styles.noClassroom}>
        <div className={styles.messageBox}>
          <h2>No Classroom Selected</h2>
          <p>Please select a classroom to use the Check-In Tool.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>Student Check-In Tool</h1>
        <p>Click on a student to begin their daily check-in</p>
      </div>

      {isLoadingStudents ? (
        <div className={styles.loadingContainer}>
          <div className={styles.loadingContent}>
            <div className={styles.spinner}></div>
            <p>Loading students...</p>
          </div>
        </div>
      ) : students.length === 0 ? (
        <div className={styles.emptyState}>
          <p>No students found in this classroom.</p>
          <p className={styles.emptyStateHint}>Add students in the Students section to get started.</p>
        </div>
      ) : (
        <StudentCheckInGrid
          students={students}
          selectedStudentId={selectedStudentId}
          onStudentSelect={handleStudentSelect}
          isLoadingCheckIns={isLoadingCheckIns}
        />
      )}

      {/* Confirmation modal for re-check-in */}
      <ConfirmationModal
        show={isConfirmModalOpen}
        handleHide={handleModalHide}
        handlePerformAction={handleConfirmReCheckIn}
        title='Student Already Checked In'
        subtitle={
          selectedStudent
            ? `${selectedStudent.firstName} ${selectedStudent.lastName || ''} has already checked in today. Do you want them to check in again?`
            : 'This student has already checked in today. Do you want them to check in again?'
        }
        cancelText='Cancel'
        confirmText='Check In Again'
      />

      {/* Check-in modal */}
      {isModalOpen && selectedStudent && (
        <ElementaryCheckInModal
          studentName={`${selectedStudent.firstName} ${selectedStudent.lastName || ''}`.trim()}
          onMoodSelect={handleMoodSelect}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedStudentId(null);
          }}
          isSubmitting={isSubmitting}
        />
      )}

      {/* Side Panel */}
      <CheckInSidePanel
        isOpen={isSidePanelOpen}
        onClose={() => setIsSidePanelOpen(false)}
        onOpen={() => setIsSidePanelOpen(true)}
        students={students}
        classroom={classroom}
      />
    </div>
  );
};

export default TeacherCheckInTool;
